import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Wrench,
  Shield,
  CheckCircle,
  ArrowRight,
  Clock,
  FileCheck,
  Smartphone,
  Radio,
  Bluetooth,
  Satellite,
  MapPin,
  AlertTriangle,
  BarChart3,
  Package,
  Users,
  DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

const ToolTrackingPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('toolTrackingPage.metaTitle')}</title>
        <meta name="description" content={t('toolTrackingPage.metaDesc')} />
        <meta name="keywords" content="tool tracking, BLE trackers, industrial tool tracking, smart lockers, tool distribution, equipment management" />
        <link rel="canonical" href="https://0gbaltics.com/tool-tracking" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* SECTION 1: HERO */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="tool-tracking-hero">
          <div className="container-0g">
            <div className="max-w-4xl">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-orange-500 mb-4">
                {t('toolTrackingPage.heroBadge')}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {t('toolTrackingPage.heroTitle')}
              </h1>
              <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-2xl">
                {t('toolTrackingPage.heroSubtitle')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  {t('toolTrackingPage.requestDemo')}
                </Button>
                <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                  <a href="#distribution">{t('toolTrackingPage.viewSolutions')}</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 2: TOOL TRACKING (BLE) */}
        {/* ========================================= */}
        <section id="tracking" className="py-20 md:py-32 bg-white" data-testid="tool-tracking-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('toolTrackingPage.bleSectionBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('toolTrackingPage.bleSectionTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('toolTrackingPage.bleSectionDesc')}
              </p>
            </div>

            {/* ROI Statistics */}
            <div className="grid md:grid-cols-4 gap-6 mb-16">
              {[
                { value: '40%', label: t('toolTrackingPage.stat1'), icon: Shield },
                { value: '25%', label: t('toolTrackingPage.stat2'), icon: BarChart3 },
                { value: '90%', label: t('toolTrackingPage.stat3'), icon: Clock },
                { value: '50%', label: t('toolTrackingPage.stat4'), icon: DollarSign },
              ].map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-slate-50 border border-slate-200 p-6 rounded-lg text-center">
                    <Icon className="h-8 w-8 text-teal-600 mx-auto mb-3" />
                    <div className="text-3xl font-bold text-teal-600 mb-2">{stat.value}</div>
                    <p className="text-slate-600 text-sm">{stat.label}</p>
                  </div>
                );
              })}
            </div>

            {/* Arguments Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="bg-slate-900 p-8 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-6">{t('toolTrackingPage.whyTrackTitle')}</h3>
                <div className="space-y-4">
                  {[
                    { title: t('toolTrackingPage.whyTrack1'), desc: t('toolTrackingPage.whyTrack1Desc') },
                    { title: t('toolTrackingPage.whyTrack2'), desc: t('toolTrackingPage.whyTrack2Desc') },
                    { title: t('toolTrackingPage.whyTrack3'), desc: t('toolTrackingPage.whyTrack3Desc') },
                    { title: t('toolTrackingPage.whyTrack4'), desc: t('toolTrackingPage.whyTrack4Desc') },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-slate-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <img 
                  src="/assets/images/ble-trackers/tool-drill.png" 
                  alt="Power drill with embedded BLE sensor" 
                  className="w-full h-auto rounded-lg mb-6"
                />
                <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                  <h4 className="font-semibold text-teal-800 mb-3">{t('toolTrackingPage.howItWorks')}</h4>
                  <div className="space-y-2 text-sm text-teal-700">
                    <p><strong>1.</strong> {t('toolTrackingPage.howStep1')}</p>
                    <p><strong>2.</strong> {t('toolTrackingPage.howStep2')}</p>
                    <p><strong>3.</strong> {t('toolTrackingPage.howStep3')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* BLE Trackers Grid */}
            <h3 className="text-xl font-semibold text-slate-900 text-center mb-8">
              {t('toolTrackingPage.bleTrackersTitle')}
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  name: t('toolTrackingPage.coinBeaconName'), 
                  desc: t('toolTrackingPage.coinBeaconDesc'),
                  image: '/assets/images/ble-trackers/m1-coin-beacon.png',
                  tags: ['BLE 5.0', 'IP67']
                },
                { 
                  name: t('toolTrackingPage.proAssetTagName'), 
                  desc: t('toolTrackingPage.proAssetTagDesc'),
                  image: '/assets/images/ble-trackers/pro-asset-tag.png',
                  tags: ['BLE + LPWAN', 'LoRaWAN / Sigfox']
                },
                { 
                  name: t('toolTrackingPage.industrialTrackerName'), 
                  desc: t('toolTrackingPage.industrialTrackerDesc'),
                  image: '/assets/images/ble-trackers/industrial-tracker.png',
                  tags: ['GPS + BLE + LTE', 'Anti-theft']
                },
              ].map((tracker, index) => (
                <div key={index} className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center hover:border-teal-500 transition-colors">
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <img src={tracker.image} alt={tracker.name} className="w-24 h-24 object-contain mx-auto" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">{tracker.name}</h4>
                  <p className="text-slate-600 text-sm mb-3">{tracker.desc}</p>
                  <div className="flex items-center justify-center gap-2">
                    {tracker.tags.map((tag, i) => (
                      <span key={i} className="bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Technology Links */}
            <div className="text-center mt-12">
              <Link to="/connectivity" className="inline-flex items-center text-teal-600 font-medium hover:underline">
                {t('toolTrackingPage.learnConnectivity')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 3: TOOLS & EQUIPMENT DISTRIBUTION */}
        {/* ========================================= */}
        <section id="distribution" className="py-20 md:py-32 bg-slate-950" data-testid="distribution-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <div className="flex items-center justify-center gap-4 mb-6">
                <img 
                  src="/assets/images/tool-tracking/samourai-logo.png" 
                  alt="SAM'OURAI - Intelligent Storage Unit Partner" 
                  className="h-16 md:h-20 w-auto"
                />
              </div>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-500 mb-4">
                {t('toolTrackingPage.officialPartner')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('toolTrackingPage.distributionTitle')}
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                {t('toolTrackingPage.distributionDesc')}
              </p>
            </div>

            {/* Arguments */}
            <div className="grid md:grid-cols-4 gap-6 mb-16">
              {[
                { icon: Shield, title: t('toolTrackingPage.secureAccess'), desc: t('toolTrackingPage.secureAccessDesc') },
                { icon: FileCheck, title: t('toolTrackingPage.fullTraceability'), desc: t('toolTrackingPage.fullTraceabilityDesc') },
                { icon: Clock, title: t('toolTrackingPage.availability247'), desc: t('toolTrackingPage.availability247Desc') },
                { icon: BarChart3, title: t('toolTrackingPage.realtimeDashboard'), desc: t('toolTrackingPage.realtimeDashboardDesc') },
              ].map((arg, index) => {
                const Icon = arg.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-14 h-14 rounded-lg bg-red-600 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <h4 className="text-white font-semibold mb-2">{arg.title}</h4>
                    <p className="text-slate-400 text-sm">{arg.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Product Grid - Row 1: 2 large cards with videos */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* X'TOWER */}
              <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="xtower-card">
                <div className="relative aspect-video bg-black">
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    className="w-full h-full object-cover"
                  >
                    <source src="/assets/videos/tool-tracking/xtower-1.mp4" type="video/mp4" />
                  </video>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-bold text-white tracking-wider">X'TOWER</span>
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {t('toolTrackingPage.xtowerTitle')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('toolTrackingPage.xtowerDesc')}
                  </p>
                </div>
              </div>

              {/* X'STORE */}
              <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="xstore-card">
                <div className="relative aspect-video bg-black">
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    poster="/assets/images/tool-tracking/x-store.png"
                    className="w-full h-full object-cover"
                  >
                    <source src="/assets/videos/tool-tracking/xstore.mp4" type="video/mp4" />
                  </video>
                  <div className="absolute top-4 right-4 bg-slate-700 px-2 py-1 rounded text-xs text-white font-medium">
                    STOCK
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-bold text-white tracking-wider">X'STORE</span>
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {t('toolTrackingPage.xstoreTitle')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('toolTrackingPage.xstoreDesc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Product Grid - Row 2: 3 smaller cards with videos */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* X'LOCKER */}
              <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="xlocker-card">
                <div className="relative aspect-video bg-black">
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    poster="/assets/images/tool-tracking/x-locker.png"
                    className="w-full h-full object-cover"
                  >
                    <source src="/assets/videos/tool-tracking/xlocker.mp4" type="video/mp4" />
                  </video>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-white tracking-wider">X'LOCKER</span>
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('toolTrackingPage.xlockerTitle')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('toolTrackingPage.xlockerDesc')}
                  </p>
                </div>
              </div>

              {/* X'SLING */}
              <div className="bg-slate-900 rounded-lg overflow-hidden" data-testid="xsling-card">
                <div className="relative aspect-video bg-black">
                  <video 
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    poster="/assets/images/tool-tracking/x-sling.png"
                    className="w-full h-full object-cover"
                  >
                    <source src="/assets/videos/tool-tracking/xsling.mp4" type="video/mp4" />
                  </video>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-white tracking-wider">X'SLING</span>
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('toolTrackingPage.xslingTitle')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('toolTrackingPage.xslingDesc')}
                  </p>
                </div>
              </div>

              {/* X'WEIGHT (Coming Soon) */}
              <div className="bg-slate-900 rounded-lg overflow-hidden relative" data-testid="xweight-card">
                <div className="relative aspect-video bg-black flex items-center justify-center">
                  <img 
                    src="/assets/images/tool-tracking/xweight.png"
                    alt="X'Weight System - Coming Soon"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-4 right-4 bg-red-600 px-3 py-1 rounded text-xs text-white font-medium">
                    {t('toolTrackingPage.comingSoon')}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-white tracking-wider">X'WEIGHT</span>
                    <span className="w-2 h-2 bg-red-500 rounded-full" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {t('toolTrackingPage.xweightTitle')}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {t('toolTrackingPage.xweightDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 4: WHY IT MATTERS + CTA */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="cta-section">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                {t('toolTrackingPage.whyMattersTitle')}
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {t('toolTrackingPage.whyMattersDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {/* Tool Tracking Benefits */}
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <h3 className="text-xl font-semibold text-teal-400 mb-4">{t('toolTrackingPage.trackEveryTool')}</h3>
                <p className="text-slate-300 mb-4">
                  {t('toolTrackingPage.trackEveryToolDesc1')}
                </p>
                <p className="text-slate-400 text-sm">
                  {t('toolTrackingPage.trackEveryToolDesc2')}
                </p>
              </div>

              {/* Distribution Benefits */}
              <div className="bg-slate-800 border border-slate-700 p-8 rounded-lg">
                <h3 className="text-xl font-semibold text-red-400 mb-4">{t('toolTrackingPage.simplifyDistribution')}</h3>
                <p className="text-slate-300 mb-4">
                  {t('toolTrackingPage.simplifyDistributionDesc1')}
                </p>
                <p className="text-slate-400 text-sm">
                  {t('toolTrackingPage.simplifyDistributionDesc2')}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-teal-600 rounded-lg p-8 md:p-12 text-center">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                {t('toolTrackingPage.readyTransform')}
              </h3>
              <p className="text-teal-100 mb-8 max-w-2xl mx-auto">
                {t('toolTrackingPage.readyTransformDesc')}
              </p>
              <Button 
                size="lg"
                className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {t('toolTrackingPage.scheduleDemo')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* SECTION 5: CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default ToolTrackingPage;
